import moment from "moment";
import Copy from "../../copy";
import { StatusBadge } from "@/pages/dashboard/_misc/columns";
import { Button } from "@/components/ui/button";
import { useSheetStore } from "@/zustand/sheetStore";
import { useGetBIN, useGetRate } from "@/hooks/use-queries";
import TopupCountdown from "@/components/topup-countdown";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";
import { useUser } from "@/zustand/store";
import { getCardTransactionDisplayAmount } from "@/lib/utils";

const typeMap2: Record<
  CardTransaction["type"],
  { label: string; className?: string }
> = {
  TransferIn: { label: "Card Deposit" },
  TransferOut: { label: "Card Withdrawal" },
  Fee_Consumption: { label: "Fee" },
  auth: { label: "Authorization", className: "text-dark-warning-300" },
  Void: { label: "Void", className: "text-dark-error-300" },
};

const TransactionDetails = ({
  transactionData,
  type,
}: {
  transactionData: Transaction | CardTransaction;
  type: string;
}) => {
  const user = useUser((state) => state.user);
  const { openSheet } = useSheetStore();
  const walletTransaction = transactionData as Transaction & { binId?: string };
  const cardTransaction = transactionData as CardTransaction;
  const { data: binsData } = useGetBIN();
  const { data: rates } = useGetRate(user?.preferredCurrency || "USD");

  const formatAmount = useFormatAmountWithCurrency();

  const matchedBin = walletTransaction.binId
    ? (binsData as any[])?.find(
        (b: any) => String(b.id) === String(walletTransaction.binId),
      )
    : null;

  const txType = walletTransaction.type as WalletTxType;
  const isCardCreation = txType === "card-creation";
  const isOutgoing =
    txType === "withdrawal" ||
    txType === "card-creation" ||
    txType === "card-withdrawal";

  if (type == "wallet")
    return (
      <div>
        <div className="text-dark-text-50 mt-4 px-2">
          <div className="mt-4 space-y-2.5">
            <TransactionType type={txType} />
            <h5 className="text-2xl font-semibold">
              {formatAmount(walletTransaction.amount)}
            </h5>
          </div>

          <div className="mt-4 mb-10">
            <DetailRow label="Id" value={walletTransaction.id} />
            {txType === "card-topup" && (
              <DetailRow label="Type" value="Card Funding" />
            )}
            {!isCardCreation && txType !== "card-topup" && (
              <DetailRow
                label="Network"
                value={walletTransaction.network || "-"}
              />
            )}
            <DetailRow
              label="Amount"
              value={String(formatAmount(walletTransaction.amount || 0))}
            />
            {!isCardCreation && txType !== "card-topup" && (
              <DetailRow
                label={`Exchange rate`}
                value={`1 ${user?.preferredCurrency || "USD"} = ${parseFloat(rates?.rate).toFixed(2)} USDT`}
              />
            )}

            {txType === "card-topup" && matchedBin && (
              <>
                <DetailRow label="BIN" value={matchedBin.bin} />
                <DetailRow label="Card Type" value={matchedBin.network} />
              </>
            )}

            <DetailRow
              label={`Fee (${walletTransaction.feePercent || 0}%)`}
              value={formatAmount(walletTransaction.feeAmount || 0)}
            />
            {!isCardCreation &&
              txType !== "card-topup" &&
              (isOutgoing ? (
                <DetailRow
                  label="Amount Sent"
                  valueComponent={
                    <span className="text-dark-error-300">
                      -{formatAmount(walletTransaction.amountReceived)}
                    </span>
                  }
                />
              ) : (
                <DetailRow
                  label="Amount Received"
                  valueComponent={
                    <span className="text-dark-success-300">
                      +{formatAmount(walletTransaction.amountReceived)}
                    </span>
                  }
                />
              ))}
            {!isCardCreation && txType !== "card-topup" && (
              <DetailRow
                label="Destination"
                copy
                value={walletTransaction.address || "-"}
              />
            )}
            {!isCardCreation && txType !== "card-topup" && (
              <DetailRow
                label="TxId"
                copy
                value={walletTransaction.transactionHash || "-"}
                valueComponent={
                  <div className="flex gap-4">
                    {walletTransaction.type !== "deposit" ||
                      (walletTransaction.type === "deposit" &&
                        walletTransaction.transactionHash !== null && (
                          <span className="">
                            {walletTransaction.transactionHash || "-"}
                          </span>
                        ))}
                    {walletTransaction.type === "deposit" &&
                      walletTransaction.status === "pending" &&
                      walletTransaction.transactionHash == null && (
                        <Button
                          onClick={() => {
                            openSheet("submitTxHash", null, {
                              depositOrderId: walletTransaction.id,
                            });
                          }}
                          className="text-dark-text-100 hover:text-dark-text-50 hover:bg-dark-card-3 rounded-full text-xs"
                          variant="outline"
                          size="sm"
                        >
                          Submit Transaction Hash
                        </Button>
                      )}
                  </div>
                }
              />
            )}
            <DetailRow label="Status" value={walletTransaction.status} />
            <DetailRow
              label="Date"
              value={moment(walletTransaction.createdAt).format(
                "DD MMM YYYY, hh:mm A",
              )}
            />
          </div>

          {txType === "card-topup" &&
            walletTransaction.status === "pending" && (
              <TopupCountdown
                createdAt={walletTransaction.createdAt}
                transactionId={walletTransaction.id}
                amount={walletTransaction.amount}
              />
            )}
        </div>
      </div>
    );

  const { amount: cardDisplayAmount, currency: cardDisplayCurrency } =
    getCardTransactionDisplayAmount(cardTransaction);

  return (
    <div>
      <div className="text-dark-text-50 mt-4 px-2">
        <div className="mt-4 space-y-2.5">
          <h3 className={typeMap2[cardTransaction.type]?.className}>
            {typeMap2[cardTransaction.type]?.label}
          </h3>
          <h5 className="text-2xl font-semibold">
            {cardDisplayAmount?.toFixed(2)} {cardDisplayCurrency}
          </h5>
        </div>

        <div className="mt-4 mb-10">
          <DetailRow label="Id" value={cardTransaction.transactionId || cardTransaction.id} />
          <DetailRow label="Type" value={cardTransaction.type} />
          <DetailRow
            label="Amount"
            value={`${cardDisplayAmount?.toFixed(2)} ${cardDisplayCurrency}`}
          />
          {cardTransaction.type === "TransferIn" ||
          cardTransaction.type === "Void" ? (
            <DetailRow
              label="Amount Received"
              valueComponent={
                <span className="text-dark-success-300">
                  {cardDisplayAmount !== 0 ? "+" : ""}
                  {cardDisplayAmount?.toFixed(2)}{" "}
                  {cardDisplayCurrency}
                </span>
              }
            />
          ) : cardTransaction.type === "TransferOut" ||
            cardTransaction.type === "auth" ? (
            <DetailRow
              label="Amount Sent"
              valueComponent={
                <span className="text-dark-error-300">
                  {cardDisplayAmount !== 0 ? "-" : ""}
                  {cardDisplayAmount?.toFixed(2)}{" "}
                  {cardDisplayCurrency}
                </span>
              }
            />
          ) : null}

          <DetailRow
            label="Merchant"
            value={cardTransaction.merchantName || "-"}
          />
          <DetailRow label="Status" value={cardTransaction.status} />
          <DetailRow
            label="Date"
            value={moment(cardTransaction.transactionTime).format(
              "DD MMM YYYY, hh:mm A",
            )}
          />
        </div>
      </div>
    </div>
  );
};

interface DetailRowProps {
  label: string;
  value?: string | number;
  valueComponent?: React.ReactNode;
  copy?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({
  label,
  value,
  valueComponent,
  copy = false,
}) => {
  if (label === "Status")
    return (
      <div className="flex items-center justify-between border-black/10 pt-2 pb-2">
        <div className="text-xs capitalize">{label}</div>
        <StatusBadge
          status={value as "completed" | "pending" | "failed"}
          className="bg-transparent !px-0"
        />
      </div>
    );
  return (
    <div className="flex items-center justify-between border-black/10 pt-2 pb-2">
      <div className="text-xs capitalize">{label}</div>
      <div className="flex items-center gap-2">
        {copy && value && value !== "-" && (
          <Copy
            icon="/icons/copy-light.svg"
            side="left"
            text={value as string}
          />
        )}
        <span className="max-w-70 text-right text-sm font-medium break-words capitalize">
          {valueComponent ? valueComponent : value}
        </span>
      </div>
    </div>
  );
};

type WalletTxType =
  | "deposit"
  | "withdrawal"
  | "transfer"
  | "card-topup"
  | "card-creation"
  | "card-withdrawal"
  | "referral-reward";

const typeMap: Record<WalletTxType, { title: string; icon: string }> = {
  deposit: {
    title: "Deposit",
    icon: "/icons/deposit.svg",
  },
  withdrawal: {
    title: "Withdrawal",
    icon: "/icons/withdrawal.svg",
  },
  transfer: {
    title: "Transfer",
    icon: "/icons/transfer.svg",
  },
  "card-topup": {
    title: "Card Topup",
    icon: "/icons/money-receive.svg",
  },
  "card-creation": {
    title: "Card Creation",
    icon: "/icons/card.svg",
  },
  "card-withdrawal": {
    title: "Card Withdrawal",
    icon: "/icons/money-send.svg",
  },
  "referral-reward": {
    title: "Referral Reward",
    icon: "/icons/referral.svg",
  },
};

const TransactionType = ({ type }: { type: WalletTxType }) => {
  const entry = typeMap[type];
  if (!entry) return null;
  return (
    <div className={`flex items-center gap-2`}>
      <div className="bg-dark-auxiliary-3 flex size-8 items-center justify-center rounded-full">
        <img className="size-4" src={entry.icon} alt={entry.title} />
      </div>
      <span className="font-semibold uppercase">{entry.title}</span>
    </div>
  );
};

export default TransactionDetails;
